import { DefaultPage } from '@shared/layout/DefaultPage'
import TabSwitcher from '@pages/TrainingConstructor/ui/tabSwitcher/ui'
import { SetStateAction, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { MyTrainings } from '@pages/TrainingConstructor/ui/MyTrainings'
import { CreateTraining } from '@pages/TrainingConstructor/ui/CreateTraining'
import { observer } from 'mobx-react'
import { InfoModal } from '@shared/layout/Modal'
import { Filters } from '@pages/TrainingConstructor/ui/filter/filters'
import { FormTrainingModal } from '@widgets/trainingModals/formTrainingModal/modal'
import { trainingConstructorStore } from '@pages/TrainingConstructor/stores'
import { ExerciseParams } from '@pages/TrainingConstructor/types'
import { SearchField } from '@ui/SearchField'
import { useExerciseFilterStore } from '@entities/exercise/model/filters.provider'
import { QuickExerciseFilters } from '@pages/TrainingConstructor/ui/filter/quickFilters'
import {
	FILTER_MUSCLE_SECTIONS,
} from '@pages/TrainingConstructor/ui/filter/filterCatalog'
import { FilterMuscleCard } from '@pages/TrainingConstructor/ui/filter/muscleCard'
import { ChevronRight } from 'lucide-react'
import { CreateTrainingResponse } from '@entities/training/types/create'
import { useHistory } from 'react-router-dom'
import { ROUTES } from '@shared/constants/routes'
import { AppNavbar } from '@shared/ui/AppNavbar'
import { getMediaUrl } from '@shared/helpers/getMediaUrl'
import {
	TrainingDetailsOverlay,
	type TrainingDetailsMuscleItem,
} from '@widgets/trainingDetailsOverlay'
import { buildExerciseParamsFromExercise } from '../lib/buildExerciseParams'
import './training-constructor.css'

type ActiveTab = 'create' | 'my_trainings'
const SELECTION_WIDGET_EXIT_MS = 630
type TrainingDetailsLocationState = {
	trainingDetailsOverlay?: boolean
	trainingDetailsOverlaySource?: 'created' | 'list'
}

const TRAINING_DETAILS_MUSCLE_ITEMS: TrainingDetailsMuscleItem[] =
	FILTER_MUSCLE_SECTIONS.flatMap((section) => section.items)

const renderTrainingDetailsMuscleCard = (muscle: TrainingDetailsMuscleItem) => (
	<FilterMuscleCard muscle={muscle} isSelected isReadonly />
)

function getActiveTabFromSearch(search: string): ActiveTab {
	const params = new URLSearchParams(search)

	if (params.has('training')) {
		return 'my_trainings'
	}

	return params.get('tab') === 'my_trainings' ? 'my_trainings' : 'create'
}

function getTrainingDetailsIdFromSearch(search: string): number | null {
	const id = Number(new URLSearchParams(search).get('training'))

	return Number.isFinite(id) && id > 0 ? id : null
}

function formatSelectedExercisesCount(count: number): string {
	const lastTwoDigits = count % 100
	const lastDigit = count % 10

	if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
		return `${count} упражнений выбрано`
	}

	if (lastDigit === 1) {
		return `${count} упражнение выбрано`
	}

	if (lastDigit >= 2 && lastDigit <= 4) {
		return `${count} упражнения выбрано`
	}

	return `${count} упражнений выбрано`
}

export const TrainingConstructorPage = observer(() => {
	const location = useLocation()
	const [activeTab, setActiveTab] = useState<ActiveTab>(() =>
		getActiveTabFromSearch(location.search),
	)
	const [showModal, setShowModal] = useState<boolean>(false)
	const [isFiltersOpen, setIsFiltersOpen] = useState<boolean>(false)
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
	const [trainingDraft, setTrainingDraft] = useState({
		title: '',
		description: '',
	})
	const filterStore = useExerciseFilterStore()
	const { addedExercises } = trainingConstructorStore
	const selectedExercisesCount = addedExercises.length
	const [isSelectionWidgetRendered, setIsSelectionWidgetRendered] =
		useState<boolean>(selectedExercisesCount > 0)
	const [isSelectionWidgetVisible, setIsSelectionWidgetVisible] =
		useState<boolean>(selectedExercisesCount > 0)
	const isCreateTab = activeTab === 'create'
	const [isCreateTrainingMounted, setIsCreateTrainingMounted] =
		useState<boolean>(isCreateTab)
	const isTrainingConstructorRoute =
		location.pathname === ROUTES.overall.trainingConstructor
	const trainingDetailsWorkoutId = isTrainingConstructorRoute
		? getTrainingDetailsIdFromSearch(location.search)
		: null
	const history = useHistory()

	useEffect(() => {
		setActiveTab(getActiveTabFromSearch(location.search))
	}, [location.search])

	useEffect(() => {
		if (isCreateTab) {
			setIsCreateTrainingMounted(true)
		}
	}, [isCreateTab])

	useEffect(() => {
		if (selectedExercisesCount > 0) {
			setIsSelectionWidgetRendered(true)
			const frameId = window.requestAnimationFrame(() => {
				window.requestAnimationFrame(() => {
					setIsSelectionWidgetVisible(true)
				})
			})
			return () => window.cancelAnimationFrame(frameId)
		}

		setIsSelectionWidgetVisible(false)
		const timer = window.setTimeout(() => {
			setIsSelectionWidgetRendered(false)
		}, SELECTION_WIDGET_EXIT_MS)

		return () => window.clearTimeout(timer)
	}, [selectedExercisesCount])

	const onCreateTrainingClick = () => {
		setIsModalOpen(true)
	}

	const changeActiveTab = (
		nextTabOrUpdater: SetStateAction<ActiveTab>,
	): void => {
		const nextTab =
			typeof nextTabOrUpdater === 'function'
				? nextTabOrUpdater(activeTab)
				: nextTabOrUpdater

		setActiveTab(nextTab)
		history.replace({
			pathname: ROUTES.overall.trainingConstructor,
			search: nextTab === 'my_trainings' ? '?tab=my_trainings' : '',
		})
	}

	const switchToMyTrainings = () => {
		changeActiveTab('my_trainings')
	}

	const getExerciseParams = (): ExerciseParams[] => {
		return addedExercises.map(buildExerciseParamsFromExercise)
	}

	const openCreatedTraining = (createdTraining: CreateTrainingResponse) => {
		setTrainingDraft({ title: '', description: '' })

		history.push({
			pathname: ROUTES.overall.trainingConstructor,
			search: `?tab=my_trainings&training=${createdTraining.id_workout}`,
			state: {
				trainingDetailsOverlay: true,
				trainingDetailsOverlaySource: 'created',
			},
		})
	}

	const closeTrainingDetailsOverlay = () => {
		const state = location.state as TrainingDetailsLocationState | undefined

		if (
			state?.trainingDetailsOverlay &&
			state.trainingDetailsOverlaySource === 'list'
		) {
			history.goBack()
			return
		}

		const params = new URLSearchParams(location.search)
		params.set('tab', 'my_trainings')
		params.delete('training')

		history.replace({
			pathname: ROUTES.overall.trainingConstructor,
			search: `?${params.toString()}`,
		})
	}

	return (
		<DefaultPage
			noPadding
			className="training-constructor-page"
			style={{
				height: '100%',
				minHeight: '100dvh',
				maxWidth: 'none',
				maxHeight: 'none',
				margin: 0,
				border: 'none',
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'stretch',
				background: '#f7f8f7',
				overflow: 'hidden',
			}}
		>
			{isCreateTab && (
				<Filters
					isFiltersOpen={isFiltersOpen}
					setIsFiltersOpen={setIsFiltersOpen}
				/>
			)}

			<div className="training-constructor-shell">
				<AppNavbar />
				<div className="training-tabs-zone">
					<TabSwitcher activeTab={activeTab} setActiveTab={changeActiveTab} />
				</div>

				{isCreateTab && (
					<section className="training-control-zone">
						<SearchField
							data={filterStore.getFtsSearchTerm()}
							setData={filterStore.setFtsSearchTerm}
							placeholder="Найти упражнение"
							iconPosition="left"
							styles={{ borderRadius: 18 }}
						/>
						<QuickExerciseFilters
							onOpenAdvancedFilters={() => setIsFiltersOpen(true)}
						/>
					</section>
				)}

				<main
					className={`training-constructor-main ${!isCreateTab ? 'is-my-trainings' : ''}`}
				>
					{activeTab === 'my_trainings' && (
						<MyTrainings onOpenExercises={() => changeActiveTab('create')} />
					)}
					{isCreateTrainingMounted && (
						<div className="training-tab-panel" hidden={!isCreateTab}>
							<CreateTraining />
						</div>
					)}
				</main>
			</div>

			{isTrainingConstructorRoute && isCreateTab && (
				<>
					{isModalOpen && (
						<FormTrainingModal
							exercises={getExerciseParams()}
							draftTitle={trainingDraft.title}
							draftDescription={trainingDraft.description}
							onDraftChange={setTrainingDraft}
							isOpen={isModalOpen}
							setIsOpen={() => {
								setIsModalOpen(false)
							}}
							onCreateSuccess={openCreatedTraining}
						/>
					)}
					{isSelectionWidgetRendered && (
						<div
							className={`training-selection-widget ${isSelectionWidgetVisible ? 'is-visible' : 'is-hidden'}`}
						>
							<div className="training-selection-content">
								<div className="training-selection-summary">
									<div className="training-selection-preview" aria-hidden="true">
										{addedExercises.slice(-3).map((exercise) => (
											<img
												key={exercise.id_exercise}
												src={getMediaUrl(exercise.media.icon)}
												alt=""
											/>
										))}
										<span className="training-selection-badge">
											{selectedExercisesCount}
										</span>
									</div>
									<div>
										<div className="training-selection-count">
											{formatSelectedExercisesCount(selectedExercisesCount)}
										</div>
										<div className="training-selection-hint">
											Вы можете добавить ещё или
											<br />
											перейти к созданию тренировки
										</div>
									</div>
								</div>
								<button
									type="button"
									className="training-create-workout-button"
									onClick={onCreateTrainingClick}
								>
									Создать тренировку
									<ChevronRight aria-hidden="true" />
								</button>
							</div>
						</div>
					)}
				</>
			)}
			{showModal && (
				<InfoModal
					setShowModal={setShowModal}
					title={'Тренировка успешно создана'}
					description={'Посмотреть тренировку можно в разделе "Мои тренировки"'}
					descriptionStyle={{ paddingInline: 30 }}
					onFirstButtonClick={switchToMyTrainings}
					buttonsAmount={1}
				/>
			)}
			{trainingDetailsWorkoutId && (
				<TrainingDetailsOverlay
					workoutId={trainingDetailsWorkoutId}
					muscleItems={TRAINING_DETAILS_MUSCLE_ITEMS}
					onDismiss={closeTrainingDetailsOverlay}
					renderMuscleCard={renderTrainingDetailsMuscleCard}
				/>
			)}
		</DefaultPage>
	)
})
